import { bound } from "@frui.ts/helpers";
import { watchBusy } from "@frui.ts/screens";
import { attachAutomaticValidator, validate } from "@frui.ts/validation";
import * as Sentry from "@sentry/react";
import type { interfaces } from "inversify";
import { Modules } from "config";
import type ChargeResponse from "entities/chargeResponse";
import LocalizationService from "services/localizationService";
import ChargesRepository from "repositories/chargesRepository";
import EventBus, { emailChanged } from "services/eventBus";
import NotificationService, { NotificationType } from "services/notificationService";
import DetailViewModel from "viewModels/detailViewModel";
import { computed } from "mobx";
import ChargeOrderStatus from "entities/chargeOrderStatus";
import ChangeEmailRequest, { ChangeEmailRequestValidationRules } from "entities/changeEmailRequest";
import type SearchChargesRequest from "entities/searchChargesRequest";

export default class ChangeEmailViewModel extends DetailViewModel<ChangeEmailRequest> {
  readonly modelName = "changeEmailRequest";
  confirmation = false;

  @computed
  get isAllowedToSendConfirmation() {
    return this.charge.orderStatus === ChargeOrderStatus.Paid;
  }

  @computed
  get originalEmail() {
    return this.charge.emailAddress;
  }

  constructor(
    private charge: ChargeResponse,
    private filter: SearchChargesRequest,
    protected localizationService: LocalizationService,
    private repository: ChargesRepository,
    private eventBus: EventBus,
    private notificationService: NotificationService
  ) {
    super(charge.chargeId);
    this.navigationName = "";
  }

  protected loadDetail() {
    const changeEmail = new ChangeEmailRequest();
    attachAutomaticValidator(changeEmail, ChangeEmailRequestValidationRules);
    return changeEmail;
  }

  @bound
  onClose() {
    this.requestClose();
  }

  @bound
  @watchBusy
  async onSubmit() {
    if (validate(this.item)) {
      try {
        let charge = await this.repository.changeEmail(this.charge.chargeId, this.item);
        const entity = await this.repository.getCharge(this.filter, this.charge.chargeId);
        if (entity.items[0]) {
          charge = { ...charge, ...entity.items[0] };
        }
        this.eventBus.publish(emailChanged({ entity: charge }));

        if (this.confirmation) {
          const forceSingleChargeReceipt = this.charge.isBulkChargeOrder;
          await this.repository.sendPaymentConfirmation(this.charge, forceSingleChargeReceipt);
        }
        this.notificationService.add({
          title: this.translate(this.confirmation ? "changedAndConfirmationSent" : "changed").replace(
            "%email%",
            charge.emailAddress ?? ""
          ),
          type: NotificationType.SUCCESS,
        });
        this.requestClose();
      } catch (e) {
        Sentry.captureException(e);
      }
    }
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.callCenter, `changeEmail.${code}`);
  }

  static Factory({ container }: interfaces.Context) {
    return (charge: ChargeResponse, filter: SearchChargesRequest) =>
      new ChangeEmailViewModel(
        charge,
        filter,
        container.get(LocalizationService),
        container.get(ChargesRepository),
        container.get(EventBus),
        container.get(NotificationService)
      );
  }
}
